import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import SEO from "./components/SEO";

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// ============== PAGES ==============
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Discover from "./pages/Discover";
import Messages from "./pages/Messages";
import Chat from "./pages/Chat";
import Playdates from "./pages/Playdates";
import Profile from "./pages/Profile";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SitterPools from "./pages/SitterPools";
import BuddyUp from "./pages/BuddyUp";
import SitterPoolDetail from "./pages/SitterPoolDetail";
import CreateBooking from "./pages/CreateBooking";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Contact from "./pages/Contact";
import Marketplace from "./pages/Marketplace";
import SenCorner from "./pages/SenCorner";
import Community from "./pages/Community";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Safeguarding from "./pages/Safeguarding";
import AccessibilityPage from "./pages/Accessibility";
import Indemnity from "./pages/Indemnity";
import ModernSlavery from "./pages/ModernSlavery";
import Chatbot from "./components/Chatbot";
// NEW PAGES
import NiamhsCorner from "./pages/NiamhsCorner";
import VenuesDirectory from "./pages/VenuesDirectory";
import MummaMate from "./pages/MummaMate";
import SafetyHub from "./pages/SafetyHub";
import AboutUs from "./pages/AboutUs";
import KidsEatFree from "./pages/KidsEatFree";
import SENFocus from "./pages/SENFocus";

// ============== AUTH PROVIDER ==============
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    // Skip auth check on admin routes - admin has its own auth
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, API }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== PROTECTED ROUTE ==============
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF0] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// ============== APP ROUTER ==============
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <SEO path={location.pathname} />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/discover" element={
        <ProtectedRoute>
          <Discover />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/messages/:conversationId" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/playdates" element={
        <ProtectedRoute>
          <Playdates />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <Subscription />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/sitter-pools" element={
        <ProtectedRoute>
          <SitterPools />
        </ProtectedRoute>
      } />
      <Route path="/buddy-up" element={
        <ProtectedRoute>
          <BuddyUp />
        </ProtectedRoute>
      } />
      <Route path="/sitter-pools/:poolId" element={
        <ProtectedRoute>
          <SitterPoolDetail />
        </ProtectedRoute>
      } />
      <Route path="/sitter-pools/:poolId/book" element={
        <ProtectedRoute>
          <CreateBooking />
        </ProtectedRoute>
      } />
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      {/* Public Routes */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/marketplace" element={
        <ProtectedRoute>
          <Marketplace />
        </ProtectedRoute>
      } />
      <Route path="/sen" element={
        <ProtectedRoute>
          <SenCorner />
        </ProtectedRoute>
      } />
      <Route path="/sen-focus" element={<SENFocus />} />
      <Route path="/community" element={
        <ProtectedRoute>
          <Community />
        </ProtectedRoute>
      } />
      {/* Legal / Policy Pages (Public) */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/safeguarding" element={<Safeguarding />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/indemnity" element={<Indemnity />} />
      <Route path="/modern-slavery" element={<ModernSlavery />} />
      {/* NEW ROUTES */}
      <Route path="/niamhs-corner" element={<NiamhsCorner />} />
      <Route path="/venues" element={<VenuesDirectory />} />
      <Route path="/kids-eat-free" element={<KidsEatFree />} />
      <Route path="/mummamate" element={
        <ProtectedRoute>
          <MummaMate />
        </ProtectedRoute>
      } />
      <Route path="/safety" element={<SafetyHub />} />
      <Route path="/about" element={<AboutUs />} />
    </Routes>
    </>
  );
}

// ============== MAIN APP ==============
function App() {
  return (
    <div className="App min-h-screen bg-[#FFF9F5]">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
          <Chatbot />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
